import { AerDropdownMenu } from "@aerstudios/aerated-aerdropdown";
import { AerInputStructure } from "@aerstudios/aerated-aerinputstructure";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import cx from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import Label from "../../Label/Label";
import { SelectLookup } from "../Lookup/SelectLookup";
import * as styles from "./SelectSingle.module.scss";

// TODO DT-2803 Dropdown

export interface SelectSingleProps {
	fieldType?: "singleSelect";
	// The id for the select
	id: string;
	// The name for the select
	name: string;
	// The label for the select
	label: string;
	// The options for the select
	options: NDS.FormOption[];
	// The value of the selected option
	value: string;
	// The function to call when an option is selected
	onChange: (value: string) => void;
	// The error message to display
	errorMessage?: string;
	// Whether the select is hidden
	hidden: boolean;

	// Whether to use the label as a placeholder
	labelAsPlaceholder?: boolean;

	// Placeholder text for the select
	placeholder?: string;

	disabled?: boolean;
	editableButton?: React.ReactNode;
	className?: string;
}

export const SelectSingle: React.FC<SelectSingleProps> = ({
	id,
	label,
	value,
	errorMessage,
	options,
	hidden,
	name,
	onChange,
	placeholder = "Please select",
	labelAsPlaceholder = false,
	disabled,
	editableButton,
	className,
}) => {
	// Single Select
	// Is a dropdown which allows the user to select a single option
	// Optionally allows the user to search to drill down to the options they want
	// The selected option is displayed in the dropdown trigger

	const [key, setKey] = useState(0);

	// This is a hack to force the radio group to re-render first time and load the incoming value.
	useEffect(() => {
		setKey((prev) => prev + 1);
	}, []);

	const triggerRef = React.createRef<HTMLButtonElement>();
	// ResizeObserver
	const [width, setWidth] = useState<number | undefined>(undefined);

	useEffect(() => {
		const resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				setWidth(entry.contentRect.width);
			}
		});
		if (triggerRef.current) {
			resizeObserver.observe(triggerRef.current);
		}
		return () => {
			resizeObserver.disconnect();
		};
	}, [triggerRef]);

	return (
		<AerInputStructure.Root
			className={cx(styles.selectWrapper, className)}
			errorMessage={errorMessage}
			hidden={hidden}
		>
			<Label id={id} label={label} name={id} />
			{editableButton ? (
				<div className={styles.editableFieldWrapper}>
					<AerDropdownMenu.Root
						key={key}
						id={id}
						className={styles.dropdown}
						style={{ width: width }}
						trigger={
							<button
								ref={triggerRef}
								type="button"
								aria-label={`Select a ${label}`}
								className={styles.selectButton}
								name={name}
								disabled={disabled}
							>
								<span className={styles.activeOption}>
									{options.find((o) => o.value === value)?.label ||
										(labelAsPlaceholder && label) ||
										placeholder}
								</span>
								<span aria-hidden="true">
									<ChevronDownIcon className={styles.icon} aria-hidden="true" />
								</span>
							</button>
						}
						hidden={hidden}
						align="start"
						alignOffset={0}
					>
						<div style={{ width: width }}>
							{!disabled &&
								options.map(
									(item: { title?: string; label: string; value: string }) =>
										item.title ? (
											<span key={item.title} className={styles.titleItem}>
												{item.title}
											</span>
										) : (
											<AerDropdownMenu.MenuItem
												key={item.value}
												defaultValue={value}
												onSelect={() => onChange(item.value)}
												className={styles.menuItem}
											>
												{item.label}
											</AerDropdownMenu.MenuItem>
										),
								)}
						</div>
					</AerDropdownMenu.Root>
					{editableButton}
				</div>
			) : (
				<AerDropdownMenu.Root
					key={key}
					id={id}
					className={styles.dropdown}
					trigger={
						<button
							ref={triggerRef}
							type="button"
							aria-label={`Select a ${label}`}
							className={styles.selectButton}
							name={name}
							disabled={disabled}
						>
							<span className={styles.activeOption}>
								{options.find((o) => o.value === value)?.label ||
									(labelAsPlaceholder && label) ||
									placeholder}
							</span>
							<span aria-hidden="true">
								<ChevronDownIcon className={styles.icon} aria-hidden="true" />
							</span>
						</button>
					}
					hidden={hidden}
					align="center"
					alignOffset={0}
				>
					<div style={{ width: width }}>
						{!disabled &&
							options.map(
								(item: { title?: string; label: string; value: string }) =>
									item.title ? (
										<span key={item.title} className={styles.titleItem}>
											{item.title}
										</span>
									) : (
										<AerDropdownMenu.MenuItem
											key={item.value}
											defaultValue={value}
											onSelect={() => onChange(item.value)}
											className={styles.menuItem}
										>
											{item.label}
										</AerDropdownMenu.MenuItem>
									),
							)}
					</div>
				</AerDropdownMenu.Root>
			)}
			<AerInputStructure.ErrorMessage />
		</AerInputStructure.Root>
	);
};

// TODO - TEST STATIC
export const SelectSingleBreeds: React.FC<
	Omit<SelectSingleProps, "options">
> = ({ ...props }) => {
	const breeds = [
		"Breed not listed",
		"Unknown breed",
		"Prefer not to say",
		"Affenpinscher",
		"Afghan Hound",
		"Airedale Terrier",
		"American Akita",
		"Japanese Akita Inu",
		"Akita (unspecified type)",
		"Alapaha Blue Blood Bulldog",
		"Alaskan Klee Kai",
		"Alaskan Malamute",
		"American Bulldog",
		"American Bully (Classic)",
		"American Bully (Pocket)",
		"American Bully (Standard)",
		"American Bully (XL type)",
		"American Cocker Spaniel",
		"American Staffordshire Terrier",
		"American Water Spaniel",
		"Anatolian Shepherd Dog",
		"Appenzell Cattle Dog (Appenzeller Sennenhund)",
		"Australian Cattle Dog",
		"Australian Kelpie",
		"Australian Shepherd",
		"Australian Silky Terrier",
		"Australian Terrier",
		"Azawakh",
		"Barbet",
		"Basenji",
		"Basset Artesien Normand",
		"Basset Bleu De Gascogne",
		"Basset Fauve De Bretagne",
		"Basset Griffon Vendeen (Grand)",
		"Basset Griffon Vendeen (Petit)",
		"Basset Hound",
		"Bavarian Mountain Hound",
		"Beagle",
		"Beauceron",
		"Bedlington Terrier",
		"Belgian Shepherd Dog (Groenendael)",
		"Belgian Shepherd Dog (Laekenois)",
		"Belgian Shepherd Dog (Malinois)",
		"Belgian Shepherd Dog (Tervueren)",
		"Belgian Shepherd Dog (unspecified type)",
		"Bergamasco",
		"Berger Picard",
		"Bernese Mountain Dog",
		"Bichon Frise",
		"Bloodhound",
		"Boerboel (South African Mastiff)",
		"Bolognese",
		"Border Terrier",
		"Borzoi",
		"Boston Terrier",
		"Bouvier Des Flandres",
		"Boxer",
		"Bracco Italiano",
		"Braque D'Auvergne (French Pointer)",
		"Briard",
		"Briquet Griffon Vendeen",
		"Brittany",
		"Bullmastiff",
		"Bully Kutta (Pakistani Mastiff)",
		"Cairn Terrier",
		"Canaan Dog",
		"Canadian Eskimo Dog",
		"Cane Corso (Italian Mastiff)",
		"Catalan Sheepdog",
		"Caucasian Shepherd Dog",
		"Cavalier King Charles Spaniel",
		"Central Asia Shepherd Dog",
		"Cesky Terrier",
		"Chesapeake Bay Retriever",
		"Chihuahua (Long Coat)",
		"Chihuahua (Smooth Coat)",
		"Chinese Crested",
		"Chodský Pes (Bohemian Shepherd or Czech Shepherd)",
		"Chow Chow",
		"Cirneco Dell'Etna",
		"Clumber Spaniel",
		"Cocker Spaniel",
		"Coonhound (unspecified type)",
		"Black and Tan Coonhound",
		"Blue Tick Coonhound",
		"Redbone Coonhound",
		"Treeing Walker Coonhound",
		"Cardigan Welsh Corgi",
		"Pembroke Welsh Corgi",
		"Bearded Collie",
		"Border Collie",
		"Rough Collie",
		"Smooth Collie",
		"Coton De Tulear",
		"Curly Coated Retriever",
		"Czechoslovakian Wolfdog",
		"Dachshund (Kaninchen / Rabbit Long Haired)",
		"Dachshund (Kaninchen / Rabbit Smooth Haired)",
		"Dachshund (Kaninchen / Rabbit Wire Haired)",
		"Dachshund (Miniature Long Haired)",
		"Dachshund (Miniature Smooth Haired)",
		"Dachshund (Miniature Wire Haired)",
		"Dachshund (Long Haired)",
		"Dachshund (Smooth Haired)",
		"Dachshund (Wire Haired)",
		"Dalmatian",
		"Dandie Dinmont Terrier",
		"Deerhound",
		"Dobermann",
		"Dogo Argentino (Argentinian Mastiff)",
		"Dogo Canario (Presa Canario or Canary Mastiff)",
		"Dogue de Bordeaux (French Mastiff)",
		"Dutch Shepherd",
		"English Bull Terrier",
		"English Bull Terrier (Miniature)",
		"English Bulldog",
		"English Setter",
		"English Springer Spaniel",
		"English Toy Terrier",
		"Entlebucher Mountain Dog",
		"Estrela Mountain Dog",
		"Eurasier",
		"Field Spaniel",
		"Fila Brasileiro (Brazilian Mastiff)",
		"Finnish Lapphund",
		"Finnish Spitz",
		"Flat Coated Retriever",
		"Fox Terrier (Smooth)",
		"Fox Terrier (Wire)",
		"Foxhound",
		"French Bulldog",
		"Frisian Pointer (Stabyhoun or Stabijhoun)",
		"German Longhaired Pointer",
		"German Shorthaired Pointer",
		"German Wirehaired Pointer",
		"German Pinscher (Standard Pinscher)",
		"German Shepherd Dog",
		"German Spitz (Klein/Miniature)",
		"German Spitz (Mittel/Medium)",
		"German Spitz (Gross/Giant)",
		"German Spitz (unspecified type)",
		"Glen of Imaal Terrier",
		"Golden Retriever",
		"Gordon Setter",
		"Grand Bleu De Gascogne",
		"Grand Griffon Vendeen",
		"Great Dane",
		"Great Swiss Mountain Dog",
		"Greek Harehound (Hellenic Hound)",
		"Greenland Dog",
		"Greyhound",
		"Griffon Bruxellois (Brussels Griffon)",
		"Petit Brabançon (Smooth-coated Brussels Griffon)",
		"Griffon Fauve De Bretagne",
		"Halden Hound",
		"Hamiltonstovare",
		"Hanoverian Scenthound",
		"Harrier",
		"Havanese",
		"Hovawart",
		"Hungarian Kuvasz",
		"Hungarian Puli",
		"Hungarian Pumi",
		"Hungarian Vizsla",
		"Hungarian Wire Haired Vizsla",
		"Huntaway (New Zealand Hunting Dog)",
		"Ibizan Hound (Podenco Ibicenco)",
		"Irish Red and White Setter",
		"Irish Setter",
		"Irish Terrier",
		"Irish Water Spaniel",
		"Irish Wolfhound",
		"Italian Greyhound",
		"Italian Spinone",
		"Italian Volpino",
		"Jack Russell Terrier",
		"Jagdterrier (German Hunting Terrier)",
		"Japanese Chin",
		"Japanese Shiba Inu",
		"Japanese Spitz",
		"Japanese Tosa (Japanese Mastiff or Tosa Inu)",
		"Jura Hound (Swiss Hound)",
		"Karelian Bear Dog",
		"Keeshond",
		"Kerry Beagle",
		"Kerry Blue Terrier",
		"King Charles Spaniel (English Toy Spaniel)",
		"Kokoni (Small Greek Domestic Dog)",
		"Komondor",
		"Kooikerhondje",
		"Korean Jindo",
		"Korthals Griffon (Wirehaired Pointing Griffon)",
		"Labrador Retriever",
		"Lagotto Romagnolo",
		"Lakeland Terrier",
		"Lancashire Heeler",
		"Landseer",
		"Lapinporokoira",
		"Leonberger",
		"Lhasa Apso",
		"Lowchen (Little Lion Dog)",
		"Lurcher type (Sighthound cross)",
		"Magyar Agár",
		"Mallorca Mastiff (Ca de Bou)",
		"Maltese",
		"Manchester Terrier",
		"Maremma Sheepdog",
		"Mastiff (English Mastiff)",
		"Miniature Pinscher",
		"Mudi",
		"Munsterlander (Small)",
		"Munsterlander (Large)",
		"Neapolitan Mastiff (Mastino Napoletano)",
		"Newfoundland",
		"Norfolk Terrier",
		"Norrbottenspitz (Nordic Spitz)",
		"Northern Inuit Dog",
		"Norwegian Buhund",
		"Norwegian Elkhound",
		"Norwich Terrier",
		"Nova Scotia Duck Tolling Retriever",
		"Old English Sheepdog",
		"Otterhound",
		"Papillon",
		"Parson Russell Terrier",
		"Patterdale Terrier type",
		"Pekingese",
		"Peruvian Hairless Dog (Miniature)",
		"Peruvian Hairless Dog (Medium)",
		"Peruvian Hairless Dog (Large)",
		"Peruvian Hairless Dog (Inca Orchid) unspecified type",
		"Phalene",
		"Pharaoh Hound",
		"Picardy Sheepdog",
		"Picardy Spaniel",
		"Pit Bull Terrier",
		"Plott Hound",
		"Plummer Terrier type",
		"Podenco Andaluz",
		"Podenco Canario",
		"Pointer (English Pointer)",
		"Polish Lowland Sheepdog",
		"Pomeranian",
		"Poodle (Toy)",
		"Poodle (Miniature)",
		"Poodle (Standard)",
		"Poodle (unspecified type)",
		"Portuguese Podengo",
		"Portuguese Pointer",
		"Portuguese Water Dog",
		"Posavac Hound",
		"Pug",
		"Pyrenean Mastiff",
		"Pyrenean Mountain Dog",
		"Pyrenean Sheepdog",
		"Ratonero Bodeguero Andaluz (Spanish Rat Terrier)",
		"Rhodesian Ridgeback",
		"Romanian Mioritic",
		"Rottweiler",
		"Russian Black Terrier",
		"Russian Toy",
		"Saluki",
		"Samoyed",
		"Schapendoes (Dutch Sheepdog)",
		"Schipperke",
		"Schnauzer (Miniature)",
		"Schnauzer (Standard)",
		"Schnauzer (Giant)",
		"Schnauzer (unspecified type)",
		"Scottish Terrier",
		"Sealyham Terrier",
		"Segugio Italiano",
		"Shar Pei",
		"Shetland Sheepdog",
		"Shih Tzu",
		"Siberian Husky",
		"Skye Terrier",
		"Sloughi",
		"Slovakian Rough Haired Pointer",
		"Soft Coated Wheaten Terrier",
		"Spanish Greyhound (Galgo Español)",
		"Spanish Mastiff (Mastin Español)",
		"Spanish Water Dog",
		"St Bernard",
		"Staffordshire Bull Terrier",
		"Sussex Spaniel",
		"Swedish Lapphund",
		"Swedish Vallhund",
		"Tamaskan type",
		"Tatra Shepherd Dog",
		"Thai Ridgeback Dog",
		"Tibetan Mastiff",
		"Tibetan Spaniel",
		"Tibetan Terrier",
		"Tornjak (Bosnian-Herzegovinian Sheepdog)",
		"Trailhound type",
		"Turkish Kangal Dog",
		"Utonagan type",
		"Weimaraner",
		"Welsh Springer Spaniel",
		"Welsh Sheepdog (Welsh Collie)",
		"Welsh Terrier",
		"West Highland White Terrier",
		"Whippet",
		"White Swiss Shepherd Dog",
		"Xoloitzcuintle (Mexican Hairless) Miniature",
		"Xoloitzcuintle (Mexican Hairless) Intermediate",
		"Xoloitzcuintle (Mexican Hairless) Standard",
		"Xoloitzcuintle (Mexican Hairless) unspecified type",
		"Yorkshire Terrier",
		"Yugoslavian Shepherd Dog (Sharplanina)",
	];

	const options = useMemo(() => {
		let optionsToReturn = breeds.map((name) => ({
			label: name,
			value: name,
		}));

		optionsToReturn = [...optionsToReturn];
		return optionsToReturn;
	}, []);

	return <SelectLookup {...props} options={options || []} />;
};
